import React, { memo, useContext } from 'react';
import { Handle } from 'react-flow-renderer';
import BotsContext from '../../context/FlowContext';
import SaveContext from '../../context/SaveContext';
import { CHANNEL_FROM_WATSON } from '../../dto/DialogDTO';
import Button from '../@base/Button';
import Tooltip from '../@base/Tooltip';
import Component from '../Component/Component';
import ComponentsType, { findComponentBy } from '../Component/ComponentType';
import ContextMenu from '../ContextMenu/ContextMenu';
import styles from './NodeFlow.module.css';

/** Nó do fluxo apresentado no canvas */
const NodeFlow = ({ data: props }) => {
  const context = useContext(BotsContext);
  const saveContext = useContext(SaveContext);

  const componentType = props.typeElement;
  const isLastComponent = props.order === props.flowLength - 1;

  const addNewNode = (event, type) => {
    event.preventDefault();
    const newNode = props.createNode(type, 0, 0);
    const newEdge = props.createEdge(props.id);
    props.addNodeFlow(newNode, newEdge);
  };

  /** Mostra o botão de edição do nó. O comportamento do botão é
   *  diferente para os componentes Inicio e Watson Folder   */
  const showEditButton = () => {
    let icon = <i className="fas fa-edit fa-sm" />;
    let title = 'Editar';
    let extraStyle = null;

    if (componentType.id === ComponentsType.WATSON_DIALOG_FOLDER.id) {
      icon = <i className="fas fa-folder-open fa-sm" />;
      title = 'Abrir/Fechar';
    } else if (componentType.id === ComponentsType.INICIO.id) {
      extraStyle = { visibility: 'hidden' };
    }

    return (
      <Button
        small
        circle
        onClick={() => props.editComponent(props.id)}
        style={{ color: componentType.secondaryColor, ...extraStyle }}
        disabled={saveContext.savingStatus.isSaving}
        title={title}
      >
        {icon}
      </Button>
    );
  };

  /** Retorna as opções possiveis para o menu de contexto do botão de adicionar */
  function getContextMenuOption() {
    let options = [];

    switch (componentType.id) {
      case ComponentsType.WATSON_ROOT.id: {
        options = [['Pasta', ComponentsType.WATSON_DIALOG_FOLDER], ['Diálogo', ComponentsType.WATSON_DIALOG]];
        break;
      }
      case ComponentsType.WATSON_DIALOG.id: {
        options = [['Pasta abaixo', ComponentsType.WATSON_DIALOG_FOLDER], ['Diálogo abaixo', ComponentsType.WATSON_DIALOG]];
        break;
      }
      case ComponentsType.WATSON_DIALOG_FOLDER.id: {
        options = [
          ['Pasta abaixo', ComponentsType.WATSON_DIALOG_FOLDER],
          ['Pasta dentro da pasta', ComponentsType.WATSON_DIALOG_FOLDER_IN_FOLDER],
          ['Diálogo abaixo', ComponentsType.WATSON_DIALOG],
          ['Diálogo dentro da pasta', ComponentsType.WATSON_DIALOG_IN_FOLDER],
        ];
        break;
      }
      default: {
        /** Opções para os componentes do fluxo principal
            são recuperados na stack do fluxo carregado no contexto  */
        const componentsBots = context.flow.stack.supportedEngineReleases;
        options = componentsBots.filter((e) => (e.engine.name !== 'Watson Assistant' && e.mandatory === false));
        options = options.map((componentBot) => {
          const component = findComponentBy('title', componentBot.engine.name);
          if (component.fixedLastPosition && !isLastComponent) {
            return null;
          }
          return [component.title, component];
        }).filter((item) => (item != null));
      }
    }
    return options;
  }

  /** Mostra o botão de adicionar do nó.
   *
   *  Quando o botão e clicado é mostrado um menu de contexto
   *  com os tipos de componentes que podem ser adicionados.
   *  Os tipos possiveis para os componetes do fluxo principal,
   *  são diferentes dos tipos possiveis para os componentes do fluxo do Watson
   *  Componentes com notAllowedEngineAfter não permitem adicionar componentes depois
   *  então o botão é desabilitado */
  const showMoreButton = () => {
    if (props.notAllowedEngineAfter) {
      return (
        <Button
          small
          circle
          disabled
        />
      );
    }

    const button = (
      <Button
        small
        circle
        disabled={!props.isValidFlow}
        style={{ color: componentType.secondaryColor }}
      >
        <i className="fa fa-plus fa-sm"> </i>
      </Button>
    );

    const options = getContextMenuOption().map((option) => (
      <a onClick={(e) => addNewNode(e, option[1])} href="/" key={option[0]}>
        {option[0]}
      </a>
    ));

    return (
      <ContextMenu
        button={button}
        options={options}
      />
    );
  };

  /** Mostra os manipuladores de entrada (local de entrada do vertice no nó) */
  const showHandleIn = () => (
    <>
      <Handle
        className={styles.HandleIn}
        type="target"
        position="left"
        id="targetHandle"
        key="targetHandle"
      />
      <Handle
        className={styles.HandleInTop}
        type="target"
        position="top"
        id="targetHandleTop"
        key="targetHandleTop"
      />
    </>
  );

  /** Mostra os manipuladores de saida (local de saida para o vertice no nó) */
  const showHandleOut = () => (
    <>
      <Handle
        id="sourceHandle"
        style={{ visibility: 'hidden' }}
        type="source"
        position="bottom"
      />
      <Handle
        className={styles.HandleOut}
        type="source"
        position="bottom"
      />
    </>
  );

  /** Mostra os erros na configuração do componente.
   *  Os erros precisam ser adicionado na propriedade errorsList do componente.data */
  const showErrors = () => {
    if (!props.errorsList || props.errorsList.length === 0) return null;
    const errors = props.errorsList.map((err) => <span key={err}>{err}</span>);
    return (
      <>
        <div className={`${styles.Tip} ${styles.Warning}`}>
          <span>
            <i className="fa fa-exclamation fa-sm" />
          </span>
          <Tooltip place="right" error="true">
            <div className="tooltipContent">
              {errors}
            </div>
          </Tooltip>
        </div>
      </>
    );
  };

  const showTags = () => {
    if (!props.tag) return null;

    return (
      <div className={`${styles.Tip} ${styles.Tag} ${props.tag === CHANNEL_FROM_WATSON.web ? styles.TagWeb : ''}`}>
        <span>
          {props.tag}
        </span>
      </div>
    );
  };

  /** Mostra o conteúdo do nó.
   *
   *  Componentes Inicial, Dialogo do Watson e Pasta Watson,
   *  mostram o conteúdo de uma forma diferente
   */
  const showContent = () => {
    const extraProps = {};
    const isWatsonFlowNode = componentType.id === ComponentsType.WATSON_DIALOG.id
      || componentType.id === ComponentsType.WATSON_DIALOG_FOLDER.id;

    if (componentType.id === ComponentsType.INICIO.id) {
      extraProps.initial = 'true';
    } else if (isWatsonFlowNode) {
      extraProps.description = props.intent;
      extraProps.larger = 'true';
    }

    return <Component type={componentType} compact="true" data={props} {...extraProps} />;
  };

  /** Mostra o cabeçalho do nó. */
  const showHeader = () => (
    <div
      className={styles.Header}
      style={{ color: componentType.secondaryColor }}
    >
      {showEditButton()}
      <span>{props.label || props.untitlePlaceholder}</span>
      {showMoreButton()}
    </div>
  );

  return (
    <div
      className={`${styles.Node} ${componentType.id.toLowerCase()}`}
      style={{ backgroundColor: componentType.primaryColor }}
    >
      {showTags()}
      {showErrors()}
      {showHeader()}
      {showContent()}
      {showHandleIn()}
      {showHandleOut()}
    </div>
  );
};

export default memo(NodeFlow);
